// src/modules/brands/hooks/useBrandActions.ts
import { useCallback } from "react";
import {
  BrandDto,
  BrandHighlightDto,
  CreateBrandInput,
  UpdateBrandInput,
} from "../models/brand.model";
import {
  updateEntityInList,
  sortByOrderIndex,
  reindexItems,
} from "@modules/shared/utils/state-updates";

// ===== Helper functions cho highlights =====
const normalizeHighlights = (items: BrandHighlightDto[]) => {
  const unique = new Map<string, BrandHighlightDto>();
  items.forEach((item) => {
    if (!item.b2b_id) return;
    const existing = unique.get(item.b2b_id);
    if (!existing || item.order_index < existing.order_index)
      unique.set(item.b2b_id, item);
  });
  return reindexItems(sortByOrderIndex(Array.from(unique.values())));
};

const reorderHighlights = (
  items: BrandHighlightDto[],
  brandId: string,
  targetIndex: number,
) => {
  const sorted = sortByOrderIndex(items);
  const currentIndex = sorted.findIndex((item) => item.b2b_id === brandId);
  if (currentIndex === -1) return reindexItems(sorted);
  const [moved] = sorted.splice(currentIndex, 1);
  sorted.splice(Math.max(0, Math.min(targetIndex, sorted.length)), 0, moved);
  return reindexItems(sorted);
};

// ===== Main hook =====
export const useBrandActions = (
  service: any,
  authArgs: any,
  setBrands: React.Dispatch<React.SetStateAction<BrandDto[]>>,
  setHighlights: React.Dispatch<React.SetStateAction<BrandHighlightDto[]>>,
  loadUserInfo: (id: string) => Promise<any>,
) => {
  // ----- Brand CRUD -----
  const createBrand = useCallback(
    async (input: CreateBrandInput) => {
      const created = await service.createBrand(input, authArgs);
      setBrands((prev) => updateEntityInList(prev, created));
      if (created.owner_id) await loadUserInfo(created.owner_id);
      return created;
    },
    [service, authArgs, setBrands, loadUserInfo],
  );

  const updateBrand = useCallback(
    async (id: string, input: UpdateBrandInput) => {
      const updated = await service.updateBrand(id, input, authArgs);
      setBrands((prev) => updateEntityInList(prev, updated));
      return updated;
    },
    [service, authArgs, setBrands],
  );

  const deleteBrand = useCallback(
    async (id: string) => {
      const success = await service.deleteBrand(id, authArgs);
      if (success) {
        setBrands((prev) => prev.filter((b) => b.id !== id));
        setHighlights((prev) => prev.filter((h) => h.b2b_id !== id));
      }
      return success;
    },
    [service, authArgs, setBrands, setHighlights],
  );

  // ----- Members -----
  const addBrandMember = useCallback(
    async (id: string, member: string, branchId: string) => {
      const updated = await service.addBrandMember(
        id,
        member,
        branchId,
        authArgs,
      );
      setBrands((prev) => updateEntityInList(prev, updated));
      await loadUserInfo(member);
      return updated;
    },
    [service, authArgs, setBrands, loadUserInfo],
  );

  const removeBrandMember = useCallback(
    async (id: string, member: string, branchId: string) => {
      const updated = await service.removeBrandMember(
        id,
        member,
        branchId,
        authArgs,
      );
      setBrands((prev) => updateEntityInList(prev, updated));
      return updated;
    },
    [service, authArgs, setBrands],
  );

  // ----- Media uploads -----
  const uploadBrandLogo = useCallback(
    async (id: string, file: File) => {
      const updated = await service.uploadBrandLogo(id, file, authArgs);
      setBrands((prev) => updateEntityInList(prev, updated));
      return updated;
    },
    [service, authArgs, setBrands],
  );

  const removeBrandLogo = useCallback(
    async (id: string, key: string) => {
      const updated = await service.removeBrandLogo(id, key, authArgs);
      setBrands((prev) => updateEntityInList(prev, updated));
      return updated;
    },
    [service, authArgs, setBrands],
  );

  const uploadBrandBanner = useCallback(
    async (id: string, file: File) => {
      const updated = await service.uploadBrandBanner(id, file, authArgs);
      setBrands((prev) => updateEntityInList(prev, updated));
      return updated;
    },
    [service, authArgs, setBrands],
  );

  const removeBrandBanner = useCallback(
    async (id: string, key: string) => {
      const updated = await service.removeBrandBanner(id, key, authArgs);
      setBrands((prev) => updateEntityInList(prev, updated));
      return updated;
    },
    [service, authArgs, setBrands],
  );

  const addBrandMedia = useCallback(
    async (id: string, file: File) => {
      const updated = await service.addBrandMedia(id, file, authArgs);
      setBrands((prev) => updateEntityInList(prev, updated));
      return updated;
    },
    [service, authArgs, setBrands],
  );

  const removeBrandMedia = useCallback(
    async (id: string, key: string) => {
      const updated = await service.removeBrandMedia(id, key, authArgs);
      setBrands((prev) => updateEntityInList(prev, updated));
      return updated;
    },
    [service, authArgs, setBrands],
  );

  // ----- Highlight actions -----
  const addHighlight = useCallback(
    async (brandId: string) => {
      const newHighlight = await service.addHighlight(brandId, authArgs);
      setHighlights((prev) =>
        normalizeHighlights([
          ...prev.filter((h) => h.b2b_id !== brandId),
          newHighlight,
        ]),
      );
    },
    [service, authArgs, setHighlights],
  );

  const removeHighlight = useCallback(
    async (brandId: string) => {
      let exists = false;
      setHighlights((prev) => {
        exists = prev.some((h) => h.b2b_id === brandId);
        return prev;
      });
      if (!exists) return false;
      const success = await service
        .removeHighlight(brandId, authArgs)
        .catch(() => false);
      if (success) {
        setHighlights((prev) =>
          normalizeHighlights(prev.filter((h) => h.b2b_id !== brandId)),
        );
      }
      return success;
    },
    [service, authArgs, setHighlights],
  );

  const reorderHighlight = useCallback(
    async (brandId: string, orderIndex: number) => {
      await service.updateHighlightIndex(brandId, orderIndex, authArgs);
      setHighlights((prev) =>
        normalizeHighlights(reorderHighlights(prev, brandId, orderIndex)),
      );
    },
    [service, authArgs, setHighlights],
  );

  // ----- Return all actions -----
  return {
    createBrand,
    updateBrand,
    deleteBrand,
    addBrandMember,
    removeBrandMember,
    uploadBrandLogo,
    removeBrandLogo,
    uploadBrandBanner,
    removeBrandBanner,
    addBrandMedia,
    removeBrandMedia,
    addHighlight,
    removeHighlight,
    reorderHighlight,
  };
};
